import { createCash } from '@/api/mutations/cash';
import { sendInvoicePdf, submitInvoice } from '@/api/mutations/invoice';
import { closeShift, createShift } from '@/api/mutations/shift';
import { selectOnline, setOnline } from '@/redux/config/configSlice';
import { removePending } from '@/redux/data/dataSlice';
import { PendingsType } from '@/redux/data/dataTypes';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { store } from '@/redux/store';
import toaster from '@/toaster';
import { focusManager, onlineManager } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to manage document visibility for React Query
 */
export const useQueryConnect = () => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      focusManager.setFocused(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return true;
};

/**
 * Hook to manage internet connection status
 */
export const useInternetConnection = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const online = useAppSelector(selectOnline);

  const handleOnline = useCallback(() => {
    onlineManager.setOnline(true);
    if (!online) {
      dispatch(setOnline(true));
      toaster.info(t('common:connection_restore'));
    }
  }, [dispatch, t, online]);

  const handleOffline = useCallback(() => {
    onlineManager.setOnline(false);
    if (online) {
      dispatch(setOnline(false));
      toaster.info(t('common:connection_lost'));
    }
  }, [dispatch, t, online]);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    onlineManager.setOnline(navigator.onLine);
    dispatch(setOnline(navigator.onLine));

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dispatch, handleOnline, handleOffline]);

  return online;
};

/**
 * Hook to handle offline mutations when connection is restored
 */
export const useOfflineMutations = () => {
  const dispatch = useAppDispatch();
  const isOnline = useAppSelector(selectOnline);
  const { isAuthenticated } = useAuth();

  const processPending = useCallback(
    async (pending: PendingsType) => {
      if (!isAuthenticated) {
        return; // Don't process if not authenticated
      }

      try {
        const data = JSON.parse(pending.data);
        let success = false;

        switch (pending.type) {
          case 'START_SHIFT':
            await createShift(data);
            success = true;
            break;
          case 'TICKET_SUBMIT':
            await submitInvoice(data);
            success = true;
            break;
          case 'INVOICE_SEND':
            await sendInvoicePdf(data);
            success = true;
            break;
          case 'ADD_CASH':
            await createCash(data);
            success = true;
            break;
          case 'CLOSE_SHIFT':
            await closeShift(data);
            success = true;
            break;
          default:
            console.warn(`Unknown pending type: ${pending.type}`);
        }

        if (success) {
          dispatch(removePending(pending));
        }
      } catch (error) {
        console.error(`Error processing ${pending.type}:`, error);
      }
    },
    [dispatch, isAuthenticated]
  );

  const startSync = useCallback(() => {
    if (!isAuthenticated) {
      return; // Don't sync if not authenticated
    }

    const pendings = store.getState().data.pendings;
    if (!pendings?.length) return;

    // Process pendings sequentially to avoid race conditions
    const processSequentially = async () => {
      for (const pending of pendings) {
        await processPending(pending);
      }
    };

    processSequentially().catch((error) => {
      console.error('Sync error:', error);
      toaster.error('common:sync_failed');
    });
  }, [processPending, isAuthenticated]);

  useEffect(() => {
    if (isOnline && isAuthenticated) {
      startSync();
    }
  }, [isOnline, startSync, isAuthenticated]);

  return startSync;
};
